import axios from 'axios'
import { validatePhoneNumber, formatPhoneNumber } from '@/utils/phoneNumber'

const LAYOUT_FIELD_TYPES = new Set(['section_header', 'divider', 'paragraph'])
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i

const publicApi = {
  withCredentials: false,
}

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isValidEmail(value) {
  return EMAIL_RE.test(String(value || '').trim())
}

/**
 * Format a phone answer using MFA/profile rules when valid.
 * Partial/invalid input is left as typed (spaces stripped).
 * @param {unknown} value
 * @returns {string}
 */
export function formatPublicFormPhone(value) {
  const raw = String(value ?? '').replace(/ /g, '')
  if (!raw.trim()) return ''
  const error = validatePhoneNumber(raw, { required: false })
  if (error) return raw
  return formatPhoneNumber(raw)
}

/**
 * @param {string} publicId
 * @returns {Promise<{ form: Record<string, unknown>|null, error: string|null, status: number|null }>}
 */
export async function fetchPublicForm(publicId) {
  const trimmed = typeof publicId === 'string' ? publicId.trim() : ''
  if (!trimmed) {
    return { form: null, error: 'Missing form id.', status: 400 }
  }

  try {
    const { data, status } = await axios.get(
      `${import.meta.env.VITE_BASE_LINK}/public/forms/${encodeURIComponent(trimmed)}`,
      publicApi,
    )
    if (data?.success && data?.form) {
      return { form: data.form, error: null, status }
    }
    return {
      form: null,
      error: data?.error || 'This form is unavailable.',
      status: status || 404,
    }
  } catch (error) {
    const status = error?.response?.status ?? null
    const message =
      error?.response?.data?.error ||
      (status === 404
        ? 'This form is not available.'
        : 'Unable to load this form. Please try again.')
    return { form: null, error: message, status }
  }
}

/**
 * Look up RefReps orgs where the email is an instructor (public, no auth).
 * @param {string} publicId
 * @param {string} email
 * @returns {Promise<{
 *   success: boolean,
 *   email?: string,
 *   count: number,
 *   organizations: Array<Record<string, string>>,
 *   error?: string,
 *   status: number|null,
 * }>}
 */
export async function lookupInstructorOrgs(publicId, email) {
  const trimmedId = typeof publicId === 'string' ? publicId.trim() : ''
  const trimmedEmail = typeof email === 'string' ? email.trim().toLowerCase() : ''
  if (!trimmedId) {
    return { success: false, count: 0, organizations: [], error: 'Missing form id.', status: 400 }
  }
  if (!isValidEmail(trimmedEmail)) {
    return { success: false, count: 0, organizations: [], error: 'Enter a valid email address', status: 400 }
  }

  try {
    const { data, status } = await axios.post(
      `${import.meta.env.VITE_BASE_LINK}/public/forms/${encodeURIComponent(trimmedId)}/lookup-instructor-orgs`,
      { email: trimmedEmail },
      publicApi,
    )
    const organizations = Array.isArray(data?.organizations) ? data.organizations : []
    return {
      success: Boolean(data?.success),
      email: data?.email || trimmedEmail,
      count: Number.isFinite(data?.count) ? data.count : organizations.length,
      organizations,
      error: data?.error,
      status,
    }
  } catch (error) {
    const status = error?.response?.status ?? null
    const data = error?.response?.data
    return {
      success: false,
      email: trimmedEmail,
      count: 0,
      organizations: [],
      error:
        data?.error ||
        (status === 429
          ? 'Too many lookups. Please try again later.'
          : status === 404
            ? 'This form is not available.'
            : 'Unable to look up organizations. Please try again.'),
      status,
    }
  }
}

/**
 * @param {string} publicId
 * @param {{
 *   answers: Record<string, unknown>,
 *   otherSpecify?: Record<string, string>,
 *   meta?: {
 *     embed?: boolean,
 *     referrer?: string,
 *     userAgent?: string,
 *     sessionId?: string,
 *     visitorId?: string,
 *     sessionActiveMs?: number,
 *   },
 *   organizationCompanyId?: string,
 *   organizationCompanyIds?: string[],
 *   organizationName?: string,
 * }} payload
 */
export async function submitPublicForm(publicId, payload) {
  const trimmed = typeof publicId === 'string' ? publicId.trim() : ''
  if (!trimmed) {
    return {
      success: false,
      error: 'Missing form id.',
      status: 400,
      errors: [],
      code: null,
      organizations: [],
    }
  }

  const body = {
    answers: payload?.answers || {},
    otherSpecify: payload?.otherSpecify || {},
    meta: payload?.meta || {},
  }
  const orgIds = Array.isArray(payload?.organizationCompanyIds)
    ? payload.organizationCompanyIds.map((id) => String(id || '').trim()).filter(Boolean)
    : []
  const orgId = String(payload?.organizationCompanyId || orgIds[0] || '').trim()
  const organizationName = String(payload?.organizationName || '').trim()

  // Manual and linked organization submissions are mutually exclusive.
  // A manual name wins over any stale IDs left in UI state.
  if (organizationName) {
    body.organizationName = organizationName
  } else {
    if (orgId) body.organizationCompanyId = orgId
    if (orgIds.length) body.organizationCompanyIds = orgIds
    else if (orgId) body.organizationCompanyIds = [orgId]
  }

  try {
    const { data, status } = await axios.post(
      `${import.meta.env.VITE_BASE_LINK}/public/forms/${encodeURIComponent(trimmed)}/submit`,
      body,
      publicApi,
    )
    return {
      success: Boolean(data?.success),
      message: data?.message,
      submissionId: data?.submissionId,
      linkedOrganization: data?.linkedOrganization || null,
      organizationSelection:
        data?.organizationSelection && typeof data.organizationSelection === 'object'
          ? data.organizationSelection
          : null,
      salesLeadId: data?.salesLeadId || null,
      matchType: data?.matchType || null,
      zoomSchedulers:
        data?.zoomSchedulers && typeof data.zoomSchedulers === 'object'
          ? data.zoomSchedulers
          : {},
      ymtcAccessPath: typeof data?.ymtcAccessPath === 'string' ? data.ymtcAccessPath : '',
      ymtcAccessToken: typeof data?.ymtcAccessToken === 'string' ? data.ymtcAccessToken : '',
      ymtcExpiresAt: typeof data?.ymtcExpiresAt === 'string' ? data.ymtcExpiresAt : '',
      ymtcAccount: typeof data?.ymtcAccount === 'string' ? data.ymtcAccount : '',
      errors: Array.isArray(data?.errors) ? data.errors : [],
      organizations: Array.isArray(data?.organizations) ? data.organizations : [],
      code: data?.code || null,
      error: data?.error,
      status,
    }
  } catch (error) {
    const status = error?.response?.status ?? null
    const data = error?.response?.data
    return {
      success: false,
      message: data?.message,
      linkedOrganization: null,
      salesLeadId: null,
      matchType: null,
      zoomSchedulers: {},
      errors: Array.isArray(data?.errors) ? data.errors : [],
      organizations: Array.isArray(data?.organizations) ? data.organizations : [],
      code: data?.code || null,
      error:
        data?.error ||
        (status === 429
          ? 'Too many submissions. Please try again later.'
          : 'Unable to submit this form. Please try again.'),
      status,
    }
  }
}

/**
 * Map form fields to org autofill targets using the same label heuristics as learn-server.
 * @param {Array<Record<string, unknown>>} fields
 * @returns {{
 *   emailClientId: string|null,
 *   orgNameClientId: string|null,
 *   stateClientId: string|null,
 *   cityClientId: string|null,
 *   addressClientId: string|null,
 *   zipClientId: string|null,
 * }}
 */
export function findOrgContactFieldIds(fields) {
  const result = {
    emailClientId: null,
    orgNameClientId: null,
    stateClientId: null,
    cityClientId: null,
    addressClientId: null,
    zipClientId: null,
  }

  for (const field of fields || []) {
    if (!field?.clientId || isLayoutField(field.type)) continue
    const label = String(field.label || '').toLowerCase()

    if (!result.emailClientId && field.type === 'email') {
      result.emailClientId = field.clientId
      continue
    }
    if (!result.stateClientId && field.type === 'state') {
      result.stateClientId = field.clientId
      continue
    }
    if (
      !result.orgNameClientId
      && (field.type === 'text' || field.type === 'textarea')
      && /(org|organization|school|company|district)/i.test(label)
      && !/email|phone|state|city|zip|address/i.test(label)
    ) {
      result.orgNameClientId = field.clientId
      continue
    }
    if (
      !result.stateClientId
      && (field.type === 'text' || field.type === 'select')
      && /\bstate\b/i.test(label)
    ) {
      result.stateClientId = field.clientId
      continue
    }
    if (!result.cityClientId && field.type === 'text' && /\bcity\b/i.test(label)) {
      result.cityClientId = field.clientId
      continue
    }
    if (!result.zipClientId && field.type === 'text' && /(zip|postal)/i.test(label)) {
      result.zipClientId = field.clientId
      continue
    }
    if (!result.addressClientId && isAddressField(field)) {
      result.addressClientId = field.clientId
    }
  }

  return result
}

/**
 * Address field for Places autocomplete (matches Jupiter FormPreviewPane).
 * @param {Record<string, unknown>|null|undefined} field
 */
export function isAddressField(field) {
  if (!field || isLayoutField(field.type)) return false
  if (field.type === 'address') return true
  if (
    ['text', 'textarea'].includes(field.type)
    && /\baddress\b/i.test(String(field.label || ''))
  ) {
    return true
  }
  return false
}

/**
 * @param {string|null|undefined} stateName
 * @returns {string} two-letter abbr or ''
 */
export function stateNameToAbbr(stateName) {
  if (!stateName) return ''
  const entry = Object.entries(US_STATE_ABBR_TO_NAME).find(
    ([, name]) => name.toLowerCase() === String(stateName).toLowerCase(),
  )
  return entry ? entry[0] : ''
}

/** Full US state names including DC — matches Jupiter form builder defaults. */
export const US_STATE_NAMES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'District of Columbia',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
]

/** Non-US locations offered by the Jupiter state field. */
export const INTERNATIONAL_LOCATION_NAMES = [
  'Australia', 'Canada', 'Germany', 'Japan', 'New Zealand', 'Oman', 'Poland', 'United Kingdom',
]

/** Canonical state-field option list: US states + DC, then countries, then Other. */
export const STATE_FIELD_OPTIONS = [
  ...US_STATE_NAMES,
  ...INTERNATIONAL_LOCATION_NAMES,
  { value: 'Other', isOther: true },
]

const US_STATE_ABBR_TO_NAME = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DC: 'District of Columbia', DE: 'Delaware',
  FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
}

/**
 * Options for a state field. Older forms were saved with 50 states only, so anything
 * shorter than the canonical list is treated as outdated and replaced.
 * @param {Record<string, unknown>} field
 */
export function stateFieldOptions(field) {
  const saved = Array.isArray(field?.options) ? field.options : []
  if (saved.length >= STATE_FIELD_OPTIONS.length) return saved
  return STATE_FIELD_OPTIONS
}

/**
 * Options to render for any field, applying the state-field fallback.
 * @param {Record<string, unknown>} field
 */
export function fieldOptions(field) {
  if (field?.type === 'state') return stateFieldOptions(field)
  return field?.options || []
}

/**
 * Canonical US state / DC name for an abbreviation or full name, else ''.
 * @param {string|null|undefined} value
 */
export function usStateFullName(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''
  const fromAbbr = US_STATE_ABBR_TO_NAME[raw.toUpperCase()]
  if (fromAbbr) return fromAbbr
  const lower = raw.toLowerCase()
  return US_STATE_NAMES.find((name) => name.toLowerCase() === lower) || ''
}

/**
 * Canonical country name when the value is one of the international options, else ''.
 * @param {string|null|undefined} value
 */
export function internationalLocationName(value) {
  const lower = String(value ?? '').trim().toLowerCase()
  if (!lower) return ''
  return INTERNATIONAL_LOCATION_NAMES.find((name) => name.toLowerCase() === lower) || ''
}

/**
 * Coerce org state (abbr or name) to a value that exists in the field's options.
 * @param {string} raw
 * @param {Array<unknown>} options
 */
export function coerceStateAnswerValue(raw, options) {
  const value = String(raw ?? '').trim()
  if (!value) return ''
  const opts = (options || []).map(getOptionValue).filter(Boolean)
  if (!opts.length) return value
  if (opts.includes(value)) return value
  const upper = value.toUpperCase()
  if (opts.includes(upper)) return upper
  const fromAbbr = US_STATE_ABBR_TO_NAME[upper]
  if (fromAbbr && opts.includes(fromAbbr)) return fromAbbr
  const lower = value.toLowerCase()
  const byName = opts.find((o) => o.toLowerCase() === lower)
  if (byName) return byName
  return value
}

/**
 * Normalize Zoom scheduler values to [{ title, url }] (legacy + new shapes).
 * Accepts: string URL | { url, embedUrl } | { title, url } | [{ title, url }, ...] | { links: [...] }
 * @param {unknown} value
 * @returns {Array<{ title: string, url: string }>}
 */
export function normalizeZoomLinks(value) {
  if (value == null || value === '') return []

  const toLink = (item) => {
    if (item == null) return null
    if (typeof item === 'string') {
      const url = item.trim()
      return url ? { title: '', url } : null
    }
    if (typeof item !== 'object' || Array.isArray(item)) return null
    const title = String(item.title || item.label || item.name || '').trim()
    const url = String(
      item.url || item.embedUrl || item.embed || item.link || item.schedulerUrl || '',
    ).trim()
    if (!url) return null
    return { title, url }
  }

  if (Array.isArray(value)) return value.map(toLink).filter(Boolean)
  if (typeof value === 'string') {
    const link = toLink(value)
    return link ? [link] : []
  }
  if (typeof value === 'object') {
    if (Array.isArray(value.links)) return normalizeZoomLinks(value.links)
    const link = toLink(value)
    return link ? [link] : []
  }
  return []
}

/**
 * True when a zoomSchedulers.cs / .sales entry has at least one usable link.
 * Supports legacy { url, embedUrl } and new { name, links: [{ title, url }] }.
 * @param {unknown} entry
 */
export function hasZoomScheduler(entry) {
  if (!entry) return false
  return normalizeZoomLinks(entry).length > 0
}

/**
 * Restrict a person's territory links to the ones this form selected.
 * An empty/missing selection keeps every link (legacy forms).
 * @param {Array<{ title: string, url: string }>} allLinks
 * @param {unknown} selectedLinks form.csZoomSchedulerLinks / form.salesZoomSchedulerLinks
 * @param {string} personName territory-resolved rep / cs name
 */
export function filterLinksForPerson(allLinks, selectedLinks, personName) {
  const links = Array.isArray(allLinks) ? allLinks : []
  const selected = Array.isArray(selectedLinks) ? selectedLinks : []
  if (!selected.length) return links

  const linkKey = (title, url) => `${String(title || '').trim()}::${String(url || '').trim()}`
  const person = String(personName || '').trim()
  const allowed = new Set(
    selected
      .filter((s) => String(s?.personName || '').trim() === person)
      .map((s) => linkKey(s?.title, s?.url)),
  )

  return links.filter((link) => allowed.has(linkKey(link.title, link.url)))
}

/**
 * Expand cs/sales scheduler payload into one thank-you CTA item per link.
 * @param {'cs'|'sales'} role
 * @param {unknown} entry
 * @param {unknown} [selectedLinks] per-form selection of { personName, title, url }
 * @returns {Array<{ key: string, heading: string, subheading: string, href: string, cta: string, role: string }>}
 */
export function expandZoomSchedulerItems(role, entry, selectedLinks) {
  if (entry == null || entry === '') return []

  let name = ''
  let links = []

  if (typeof entry !== 'object' || Array.isArray(entry)) {
    links = normalizeZoomLinks(entry)
  } else if (Array.isArray(entry.links)) {
    name = String(entry.name || '').trim()
    links = normalizeZoomLinks(entry.links)
  } else if (entry.url || entry.embedUrl || entry.link || entry.embed) {
    // Legacy single-link object — do not treat person `name` as the link title
    name = String(entry.name || '').trim()
    links = normalizeZoomLinks({
      title: entry.title || entry.label || '',
      url: entry.url || entry.embedUrl || entry.embed || entry.link || '',
    })
  } else {
    name = String(entry.name || '').trim()
    links = normalizeZoomLinks(entry)
  }

  if (!links.length) return []

  links = filterLinksForPerson(links, selectedLinks, name)
  if (!links.length) return []

  const roleLabel = role === 'cs' ? 'Client Success' : 'Sales'
  const fallbackTitle = name ? `Schedule with ${name}` : 'Schedule a meeting'

  return links.map((link, index) => {
    const title = String(link.title || '').trim() || fallbackTitle
    const showNameLine = Boolean(name) && title !== fallbackTitle
    return {
      key: `${role}-${index}-${link.url}`,
      heading: title,
      subheading: showNameLine ? `Schedule with ${name} · ${roleLabel}` : roleLabel,
      href: link.url,
      cta: title,
      role,
    }
  })
}

/**
 * @param {string} type
 * @returns {boolean}
 */
export function isLayoutField(type) {
  return LAYOUT_FIELD_TYPES.has(type)
}

/**
 * @param {unknown} opt
 * @returns {string}
 */
export function getOptionValue(opt) {
  if (opt == null) return ''
  if (typeof opt === 'string') return opt
  return String(opt.value ?? opt.label ?? '')
}

/**
 * @param {unknown} opt
 * @returns {boolean}
 */
export function isOtherOption(opt) {
  if (typeof opt === 'object' && opt?.isOther) return true
  const val = getOptionValue(opt).toLowerCase()
  return val === 'other' || val.startsWith('other ')
}

/**
 * Build empty answer / other maps for a form's fields.
 * @param {Array<Record<string, unknown>>} fields
 */
export function defaultCheckboxAnswers(field) {
  if (!field?.selectAllByDefault) return []
  return (field.options || [])
    .filter((opt) => !isOtherOption(opt))
    .map((opt) => getOptionValue(opt).trim())
    .filter(Boolean)
}

export function buildEmptyAnswers(fields) {
  const answers = {}
  const other = {}
  ;(fields || []).forEach((field) => {
    if (isLayoutField(field.type)) return
    if (field.type === 'name') answers[field.clientId] = { first: '', last: '' }
    else if (field.type === 'checkbox') answers[field.clientId] = defaultCheckboxAnswers(field)
    else if (field.type === 'scale') {
      // Match Jupiter preview: default to first option label (API stores labels)
      answers[field.clientId] = getOptionValue((field.options || [])[0]) || ''
    } else if (field.type === 'file') answers[field.clientId] = null
    else answers[field.clientId] = ''
    other[field.clientId] = ''
  })
  return { answers, other }
}

/**
 * Client-side validation for required fields + email format + Other specify.
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} answers
 * @param {Record<string, string>} other
 * @returns {Record<string, string>}
 */
export function validatePublicFormAnswers(form, answers, other) {
  const errors = {}
  if (form?.validateOnSubmit === false) return errors

  ;(form?.fields || []).forEach((field) => {
    if (isLayoutField(field.type)) return
    if (field.type === 'file') {
      if (field.required) {
        errors[field.clientId] = field.errorMessage || 'File uploads are not supported yet'
      }
      return
    }

    const val = answers[field.clientId]
    const fallback = field.errorMessage || 'This field is required'
    const empty =
      val === undefined ||
      val === null ||
      (typeof val === 'string' && !val.trim()) ||
      (Array.isArray(val) && val.length === 0)

    if (field.type === 'name') {
      if (field.required && (!val?.first?.trim() || !val?.last?.trim())) {
        errors[field.clientId] = fallback
      }
    } else if (field.type === 'checkbox') {
      if (field.required && !val?.length) errors[field.clientId] = fallback
    } else if (field.type === 'email') {
      if (field.required && empty) {
        errors[field.clientId] = fallback
      } else if (!empty && !EMAIL_RE.test(String(val).trim())) {
        errors[field.clientId] = field.errorMessage || 'Enter a valid email address'
      }
    } else if (field.type === 'phone') {
      // Same rules as MFA/profile: 10–15 digits, US or international (no leading + required)
      const phoneError = validatePhoneNumber(val, { required: Boolean(field.required) })
      if (phoneError) {
        errors[field.clientId] = field.errorMessage || phoneError
      }
    } else if (field.required && empty) {
      errors[field.clientId] = fallback
    }

    if (showsOtherForField(field, answers) && !String(other?.[field.clientId] ?? '').trim()) {
      errors[field.clientId] = field.errorMessage || 'Please specify your "Other" response'
    }
  })

  return errors
}

/**
 * @param {Record<string, unknown>} field
 * @param {Record<string, unknown>} answers
 */
export function showsOtherForField(field, answers) {
  if (field.type === 'checkbox') {
    const selected = answers[field.clientId] || []
    return fieldOptions(field).some(
      (o) => isOtherOption(o) && selected.includes(getOptionValue(o)),
    )
  }
  if (!['select', 'radio', 'yes_no', 'state'].includes(field.type)) return false
  const val = answers[field.clientId]
  return fieldOptions(field).some(
    (o) => isOtherOption(o) && getOptionValue(o) === val,
  )
}

/**
 * Strip empty optional answers before submit; keep required structure for name/checkbox.
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} answers
 * @param {Record<string, string>} other
 */
export function buildSubmitPayload(form, answers, other) {
  const outAnswers = {}
  const outOther = {}

  ;(form?.fields || []).forEach((field) => {
    if (isLayoutField(field.type) || field.type === 'file') return
    const clientId = field.clientId
    const val = answers[clientId]

    if (field.type === 'name') {
      const first = String(val?.first ?? '').trim()
      const last = String(val?.last ?? '').trim()
      if (!first && !last) return
      outAnswers[clientId] = { first, last }
    } else if (field.type === 'checkbox') {
      const selected = Array.isArray(val) ? val.filter(Boolean) : []
      if (!selected.length) return
      outAnswers[clientId] = selected
    } else if (field.type === 'number') {
      if (val === '' || val === null || val === undefined) return
      const num = typeof val === 'number' ? val : Number(String(val).trim())
      if (!Number.isFinite(num)) {
        outAnswers[clientId] = val
      } else {
        outAnswers[clientId] = num
      }
    } else if (field.type === 'phone') {
      if (val === undefined || val === null || String(val).trim() === '') return
      outAnswers[clientId] = formatPublicFormPhone(val)
    } else if (val === undefined || val === null || String(val).trim() === '') {
      return
    } else {
      outAnswers[clientId] = typeof val === 'string' ? val.trim() : val
    }

    if (showsOtherForField(field, answers)) {
      const text = String(other?.[clientId] ?? '').trim()
      if (text) outOther[clientId] = text
    }
  })

  return { answers: outAnswers, otherSpecify: outOther }
}
